// Story 8.10 (ADO-2019), DV-39: how many handheld (HHT) orders wait, in a Zustand store.
//
// One cheap read, `GET /api/v1/hht-orders/waiting-count` (one database count, no catalogue or
// availability read), feeds both headers' "HHT Orders" link and badge and the /choose card's
// title and button, so a failure of the classified details read (`useHhtOrdersStore`) never
// hides them. It is read:
//   - once after sign-in (`startHhtOrdersLoadOnAuth`, wired at the composition root);
//   - on every route change, at most once a minute (`refreshIfStale`), and when the browser
//     tab regains focus (`load`), both from `HhtWaitingCountRefresher`, so a file uploaded
//     while the Shopper browses shows without visiting /choose (SL1.0 recounted on every
//     page);
//   - after an accept (`markAccepted`), which takes the accepted orders off the count at once
//     and re-reads FRESH: a read that started before the accept still counts them, so it is
//     neither joined nor allowed to land.
//
// A failed read degrades silently: the store keeps what it last had (nothing, before the
// first success), so the header shows no badge rather than an error, and sign-in and the
// depot gate never wait on it. The clock is passed in (`Date.now` by default) so specs pin it.

import { create } from 'zustand';
import { getHhtWaitingCount } from '../api/hht-orders.client';

/** The route-change refresh reads the count at most this often. */
export const HHT_WAITING_COUNT_REFRESH_INTERVAL_MS = 60_000;

interface HhtWaitingCountStoreState {
  /** Whether the HHT workflow applies to the caller (server-decided, story D-8). */
  readonly applies: boolean;
  /** How many orders wait for the caller's Customer (0 when the workflow does not apply). */
  readonly waitingCount: number;
  /** (Re)read the count now. Concurrent calls share the request in flight. Never rejects. */
  load: (nowMs?: number) => Promise<void>;
  /** Read the count unless a read started less than a minute ago. Never rejects. */
  refreshIfStale: (nowMs?: number) => Promise<void>;
  /**
   * Orders were accepted: take them off the count now, and re-read it with a NEW request,
   * discarding any read already in flight (it started before the accept). Idempotent per
   * order id until `reset`: an order already counted off is not counted again (an effect
   * that runs twice must not drop the badge by two). Never rejects.
   */
  markAccepted: (hhtOrderIds: readonly string[], nowMs?: number) => Promise<void>;
  /** Session hygiene: drop everything (sign-out, account switch). */
  reset: () => void;
}

const EMPTY = { applies: false, waitingCount: 0 };

// The request in flight, so a second caller joins it; a generation bumped by `reset` (a
// sign-out) and `markAccepted` (an accept) so a read started before either cannot land
// afterwards; and when the last read started, for the route-change throttle.
let inFlight: Promise<void> | null = null;
let generation = 0;
let lastReadStartedAt: number | null = null;
// The order ids already counted off since the last `reset`.
const countedOff = new Set<string>();

export const useHhtWaitingCountStore = create<HhtWaitingCountStoreState>((set, get) => ({
  ...EMPTY,

  load(nowMs = Date.now()) {
    if (inFlight !== null) return inFlight;
    const started = generation;
    lastReadStartedAt = nowMs;
    const run: Promise<void> = (async () => {
      try {
        const answer = await getHhtWaitingCount();
        if (started !== generation) return;
        set({ applies: answer.applies, waitingCount: answer.waitingCount });
      } catch {
        // Silent: keep the last good count (none before the first success).
      }
    })();
    inFlight = run;
    void run.finally(() => {
      if (inFlight === run) inFlight = null;
    });
    return run;
  },

  refreshIfStale(nowMs = Date.now()) {
    if (
      lastReadStartedAt !== null &&
      nowMs - lastReadStartedAt < HHT_WAITING_COUNT_REFRESH_INTERVAL_MS
    ) {
      return Promise.resolve();
    }
    return get().load(nowMs);
  },

  markAccepted(hhtOrderIds, nowMs = Date.now()) {
    const newlyAccepted = hhtOrderIds.filter((hhtOrderId) => !countedOff.has(hhtOrderId));
    if (newlyAccepted.length === 0) return Promise.resolve();
    for (const hhtOrderId of newlyAccepted) countedOff.add(hhtOrderId);
    const acceptedCount = newlyAccepted.length;
    generation += 1;
    inFlight = null;
    set((state) => ({ waitingCount: Math.max(0, state.waitingCount - acceptedCount) }));
    return get().load(nowMs);
  },

  reset() {
    generation += 1;
    inFlight = null;
    lastReadStartedAt = null;
    countedOff.clear();
    set({ ...EMPTY });
  },
}));
